/* eslint-disable indent */
/* eslint-disable react-hooks/exhaustive-deps */
// Modules
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
// Application
import { getQueryParamsValue } from 'utils'
import {
    DEFAULT_MESSAGE_NEW_LOGIN,
    DEFAULT_TITLE_NEW_LOGIN,
    brandLayouts,
    defaultMessage,
    laboratoryMessage,
} from 'redesign/constants'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { CurrentLaboratoryType } from 'typings/auth'
import { STORAGE_CURRENT_LABORATORY } from 'constants/storageKeys'

type currentLabType = {
    name: string
    image: string
    title?: string
    titleBgColor?: string
    prefix?: string
    param?: string
    layout?: string
    ABTest?: string
}

type firebaseValueType = {
    [key: string]: string
}

type useCurrentLaboratoryType = {
    laboratories: currentLabType
    titles: firebaseValueType
    subtitles: firebaseValueType
}

export enum ACCESS_TYPE {
    agendamento_com_carrinho = 'agendamento com carrinho',
    agendamento_sem_carrinho = 'agendamento sem carrinho',
    agendamento_sem_carrinho_CIP = 'agendamento sem carrinho CIP',
    resultado = 'resultado',
}

export const useCurrentLaboratory = () => {
    const location = useLocation()
    const cta = getQueryParamsValue(location, 'cta') as string
    const { getConfigByName, isFeatureEnabled } = useRemoteConfig()

    const firebaseData = (getConfigByName('AUTH_LAB_DATA') as unknown) as useCurrentLaboratoryType
    const titles = (firebaseData?.titles as unknown) as firebaseValueType
    const subtitles = (firebaseData?.subtitles as unknown) as firebaseValueType

    const remoteLaboratories = (firebaseData.laboratories as unknown) as {
        [key: string]: currentLabType
    }

    const labParam = getQueryParamsValue(location, 'laboratorio') as string
    const lab = remoteLaboratories?.[labParam]

    const [layoutType, setLayoutType] = useState<ACCESS_TYPE | null>(null)
    const productIds = getQueryParamsValue(location, 'productIds')
    const redirectTo = getQueryParamsValue(location, 'redirectTo')
    const dasaMedium = getQueryParamsValue(location, 'dasa_medium')

    function getLayout() {
        const isAgendamento =
            redirectTo?.includes('agd-exames') ||
            redirectTo?.includes('vacinas') ||
            redirectTo?.includes('agendamento-movel') ||
            redirectTo?.includes('agd') ||
            redirectTo?.includes('agendamento')

        if (isAgendamento && productIds) {
            setLayoutType(ACCESS_TYPE.agendamento_com_carrinho)
            return
        }

        if (isAgendamento && !productIds && dasaMedium !== 'site') {
            setLayoutType(ACCESS_TYPE.agendamento_sem_carrinho)
            return
        }

        if (isAgendamento && !productIds && dasaMedium === 'site') {
            setLayoutType(ACCESS_TYPE.agendamento_sem_carrinho_CIP)
            return
        }

        setLayoutType(ACCESS_TYPE.resultado)
    }

    const currentLaboratory = useMemo(() => {
        if (!isFeatureEnabled('AUTH_SHOW_LAB_LOGO')) {
            return null
        }

        const validParams = ['laboratorio', 'hospital', 'utm_medium']

        const hasAlternativeParamLabKey = Object.keys(remoteLaboratories || {}).find(labKey => {
            const paramName = remoteLaboratories?.[labKey]?.param as string
            return validParams.includes(paramName) && getQueryParamsValue(location, paramName)
        }) as string

        // Para quando usar um parâmetro diferente de laboratorio, assim como o laminasc
        // que usa o parâmetro utm_medium com valor laminasc
        const alternativeLabKey = remoteLaboratories?.[hasAlternativeParamLabKey]?.param
        const alternativeLabKeyValue = alternativeLabKey ? getQueryParamsValue(location, alternativeLabKey) : undefined
        const alternativeLab = alternativeLabKey && alternativeLabKeyValue ? remoteLaboratories[alternativeLabKeyValue] : undefined

        return alternativeLab ?? lab
    }, [isFeatureEnabled, location, remoteLaboratories]) as currentLabType

    const currentLayoutInUse = () => {
        const validLayouts = Object.values(brandLayouts)
        const layout = currentLaboratory?.layout

        const layouts = {
            brandEmphasis: currentLaboratory && layout === brandLayouts.brandEmphasis,
            newLogin: currentLaboratory && layout === brandLayouts.newLogin,
            singleBrand: currentLaboratory && layout === brandLayouts.singleBrand,
            default: !validLayouts.includes(layout!) || !currentLaboratory,
        }

        return layouts
    }

    const currentLayout = useMemo(() => {
        const layouts = currentLayoutInUse()

        return layouts
    }, [])

    const title = useMemo(() => {
        const { brandEmphasis, newLogin } = currentLayoutInUse()

        if (!currentLaboratory?.name) {
            return defaultMessage
        }

        if (brandEmphasis || newLogin) {
            const remoteDefaultTitle = titles?.[('default' as unknown) as string]
            const remoteTitle = titles?.[(cta as unknown) as string] ?? remoteDefaultTitle

            return remoteTitle ?? DEFAULT_TITLE_NEW_LOGIN
        }

        return (currentLaboratory?.title || laboratoryMessage)
            .replace('{prefix}', currentLaboratory?.prefix ?? 'do')
            .replace('{laboratory}', currentLaboratory.name)
    }, [])

    const subTitle = useMemo(() => {
        const remoteDefaultSubtitle = subtitles?.[('default' as unknown) as string]
        const remoteTitle = subtitles?.[(cta as unknown) as string] ?? remoteDefaultSubtitle
        return remoteTitle ?? DEFAULT_MESSAGE_NEW_LOGIN
    }, [])

    const currentLaboratoryStorage: CurrentLaboratoryType = currentLaboratory
        ? currentLaboratory
        : JSON.parse(sessionStorage.getItem(STORAGE_CURRENT_LABORATORY) || '{}')

    useEffect(() => {
        getLayout()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return {
        title,
        subTitle,
        currentLayout,
        currentLaboratory,
        layoutType,
        currentLaboratoryStorage,
    }
}
